import type { Space, Venue } from '@Types/venue';
import { cn } from '@Utils/cn';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  EB_CORP_OPTIONAL_LABELS,
  EB_CORP_REQUIRED_LABELS,
  EB_PD_OPTIONAL_LABELS,
  EB_PD_REQUIRED_LABELS,
  EB_SHARED_OPTIONAL_LABELS,
  EB_SHARED_REQUIRED_LABELS,
  evaluateEventBookingForSpace,
  evaluateEventBookingForVenue,
} from './features/eventBooking';
import MatrixCell from './MatrixCell';
import { FeatureOnOffBadge, VenueRollupBadge } from './StatusBadge';

export type EBMatrixRow =
  | { kind: 'venue'; venue: Venue; spaceCount: number; onCount: number }
  | { kind: 'space'; venue: Venue; space: Space };

const STICKY_LABEL_CELL =
  'sticky left-0 z-10 bg-white border-r border-zinc-200';

const HEADER_CELL = 'px-3 py-2 text-xs font-medium text-zinc-600 text-center';

const SECTION_BORDER = 'border-l border-zinc-200';
const OPTIONAL_SECTION_BORDER = 'border-l-2 border-zinc-300';

const REQ_SHARED = EB_SHARED_REQUIRED_LABELS.length; // 4
const REQ_PD = 1 + EB_PD_REQUIRED_LABELS.length; // PD Status + 2
const REQ_CORP = 1 + EB_CORP_REQUIRED_LABELS.length; // Corp Status + 2
const OPT_SHARED = EB_SHARED_OPTIONAL_LABELS.length; // 4
const OPT_PD = EB_PD_OPTIONAL_LABELS.length; // 3
const OPT_CORP = EB_CORP_OPTIONAL_LABELS.length; // 3

const TOTAL_FEATURE_COLS =
  1 + REQ_SHARED + REQ_PD + REQ_CORP + OPT_SHARED + OPT_PD + OPT_CORP;

export default function EventBookingMatrix({ rows }: { rows: EBMatrixRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th
              rowSpan={3}
              className={cn(
                STICKY_LABEL_CELL,
                'border-b border-zinc-200 px-3 py-2 text-left text-xs font-medium text-zinc-600',
              )}
            >
              Venue / Space
            </th>
            <th
              colSpan={TOTAL_FEATURE_COLS}
              className="border-b border-l border-zinc-200 bg-zinc-50 px-3 py-2 text-center text-xs font-semibold text-zinc-700"
            >
              Event Booking
            </th>
          </tr>
          <tr>
            <th
              rowSpan={2}
              className={cn(HEADER_CELL, 'border-b border-l border-zinc-200')}
            >
              Status
            </th>
            <th
              colSpan={REQ_SHARED}
              className={cn(
                HEADER_CELL,
                'border-b border-zinc-200',
                SECTION_BORDER,
              )}
            >
              Required — Shared
            </th>
            <th
              colSpan={REQ_PD}
              className={cn(
                HEADER_CELL,
                'border-b border-zinc-200',
                SECTION_BORDER,
              )}
            >
              Required — Private Dining
            </th>
            <th
              colSpan={REQ_CORP}
              className={cn(
                HEADER_CELL,
                'border-b border-zinc-200',
                SECTION_BORDER,
              )}
            >
              Required — Corporate Dining
            </th>
            <th
              colSpan={OPT_SHARED}
              className={cn(
                HEADER_CELL,
                'border-b border-zinc-200',
                OPTIONAL_SECTION_BORDER,
              )}
            >
              Optional — Shared
            </th>
            <th
              colSpan={OPT_PD}
              className={cn(
                HEADER_CELL,
                'border-b border-zinc-200',
                SECTION_BORDER,
              )}
            >
              Optional — Private Dining
            </th>
            <th
              colSpan={OPT_CORP}
              className={cn(
                HEADER_CELL,
                'border-b border-zinc-200',
                SECTION_BORDER,
              )}
            >
              Optional — Corporate Dining
            </th>
          </tr>
          <tr>
            {EB_SHARED_REQUIRED_LABELS.map((label, i) => (
              <th
                key={label}
                className={cn(
                  HEADER_CELL,
                  'border-b border-zinc-200',
                  i === 0 && SECTION_BORDER,
                )}
              >
                {label}
              </th>
            ))}
            <th
              className={cn(
                HEADER_CELL,
                'border-b border-zinc-200',
                SECTION_BORDER,
              )}
            >
              PD Status
            </th>
            {EB_PD_REQUIRED_LABELS.map((label) => (
              <th
                key={label}
                className={cn(HEADER_CELL, 'border-b border-zinc-200')}
              >
                {label}
              </th>
            ))}
            <th
              className={cn(
                HEADER_CELL,
                'border-b border-zinc-200',
                SECTION_BORDER,
              )}
            >
              Corp Status
            </th>
            {EB_CORP_REQUIRED_LABELS.map((label) => (
              <th
                key={label}
                className={cn(HEADER_CELL, 'border-b border-zinc-200')}
              >
                {label}
              </th>
            ))}
            {EB_SHARED_OPTIONAL_LABELS.map((label, i) => (
              <th
                key={label}
                className={cn(
                  HEADER_CELL,
                  'border-b border-zinc-200',
                  i === 0 && OPTIONAL_SECTION_BORDER,
                )}
              >
                {label}
              </th>
            ))}
            {EB_PD_OPTIONAL_LABELS.map((label, i) => (
              <th
                key={label}
                className={cn(
                  HEADER_CELL,
                  'border-b border-zinc-200',
                  i === 0 && SECTION_BORDER,
                )}
              >
                {label}
              </th>
            ))}
            {EB_CORP_OPTIONAL_LABELS.map((label, i) => (
              <th
                key={label}
                className={cn(
                  HEADER_CELL,
                  'border-b border-zinc-200',
                  i === 0 && SECTION_BORDER,
                )}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) =>
            row.kind === 'venue' ? (
              <VenueRow key={`v-${row.venue.id}`} row={row} />
            ) : (
              <SpaceRow
                key={`s-${row.space.id}`}
                venue={row.venue}
                space={row.space}
              />
            ),
          )}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={1 + TOTAL_FEATURE_COLS}
                className="px-3 py-8 text-center text-sm text-zinc-500"
              >
                No venues to display.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ResourceLink({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 hover:underline"
    >
      {children}
      <ExternalLink className="size-3.5 opacity-60" />
    </Link>
  );
}

function dataCellClass(rowKind: 'venue' | 'space', isSection = false) {
  return cn(
    rowKind === 'venue'
      ? 'border-b border-zinc-200'
      : 'border-b border-zinc-100',
    'px-3 py-2 text-center',
    isSection && SECTION_BORDER,
  );
}

function optionalDataCellClass(rowKind: 'venue' | 'space', isSection = false) {
  return cn(
    rowKind === 'venue'
      ? 'border-b border-zinc-200'
      : 'border-b border-zinc-100',
    'px-3 py-2 text-center',
    isSection && OPTIONAL_SECTION_BORDER,
  );
}

function VenueRow({ row }: { row: Extract<EBMatrixRow, { kind: 'venue' }> }) {
  const ev = evaluateEventBookingForVenue(row.venue);
  return (
    <tr className="bg-zinc-50/60">
      <td
        className={cn(
          STICKY_LABEL_CELL,
          'border-b border-zinc-200 bg-zinc-50/60 px-3 py-2 font-semibold text-zinc-900',
        )}
      >
        <ResourceLink to={`/venues/${row.venue.id}`}>
          {row.venue.name}
        </ResourceLink>
      </td>
      <td className={cn(dataCellClass('venue'), 'border-l border-zinc-200')}>
        <VenueRollupBadge onCount={row.onCount} spaceCount={row.spaceCount} />
      </td>
      {ev.sharedRequired.map((cell, i) => (
        <td key={`shared-req-${i}`} className={dataCellClass('venue', i === 0)}>
          <MatrixCell state={cell} />
        </td>
      ))}
      <td className={dataCellClass('venue', true)}>
        <MatrixCell state={{ kind: 'na' }} />
      </td>
      {ev.pdRequired.map((cell, i) => (
        <td key={`pd-req-${i}`} className={dataCellClass('venue')}>
          <MatrixCell state={cell} />
        </td>
      ))}
      <td className={dataCellClass('venue', true)}>
        <MatrixCell state={{ kind: 'na' }} />
      </td>
      {ev.corpRequired.map((cell, i) => (
        <td key={`corp-req-${i}`} className={dataCellClass('venue')}>
          <MatrixCell state={cell} />
        </td>
      ))}
      {ev.sharedOptional.map((cell, i) => (
        <td
          key={`shared-opt-${i}`}
          className={optionalDataCellClass('venue', i === 0)}
        >
          <MatrixCell state={cell} />
        </td>
      ))}
      {ev.pdOptional.map((cell, i) => (
        <td key={`pd-opt-${i}`} className={dataCellClass('venue', i === 0)}>
          <MatrixCell state={cell} />
        </td>
      ))}
      {ev.corpOptional.map((cell, i) => (
        <td key={`corp-opt-${i}`} className={dataCellClass('venue', i === 0)}>
          <MatrixCell state={cell} />
        </td>
      ))}
    </tr>
  );
}

function SpaceRow({ venue, space }: { venue: Venue; space: Space }) {
  const ev = evaluateEventBookingForSpace(venue, space);
  return (
    <tr className="hover:bg-zinc-50">
      <td
        className={cn(
          STICKY_LABEL_CELL,
          'border-b border-zinc-100 py-2 pr-3 pl-8 text-zinc-700',
        )}
      >
        <ResourceLink to={`/spaces/${space.id}`}>{space.name}</ResourceLink>
      </td>
      <td className={cn(dataCellClass('space'), 'border-l border-zinc-200')}>
        <FeatureOnOffBadge on={ev.eventBookingOn} />
      </td>
      {ev.sharedRequired.map((cell, i) => (
        <td key={`shared-req-${i}`} className={dataCellClass('space', i === 0)}>
          <MatrixCell state={cell} />
        </td>
      ))}
      <td className={dataCellClass('space', true)}>
        <FeatureOnOffBadge on={ev.pdOn} />
      </td>
      {ev.pdRequired.map((cell, i) => (
        <td key={`pd-req-${i}`} className={dataCellClass('space')}>
          <MatrixCell state={cell} />
        </td>
      ))}
      <td className={dataCellClass('space', true)}>
        <FeatureOnOffBadge on={ev.corpOn} />
      </td>
      {ev.corpRequired.map((cell, i) => (
        <td key={`corp-req-${i}`} className={dataCellClass('space')}>
          <MatrixCell state={cell} />
        </td>
      ))}
      {ev.sharedOptional.map((cell, i) => (
        <td
          key={`shared-opt-${i}`}
          className={optionalDataCellClass('space', i === 0)}
        >
          <MatrixCell state={cell} />
        </td>
      ))}
      {ev.pdOptional.map((cell, i) => (
        <td key={`pd-opt-${i}`} className={dataCellClass('space', i === 0)}>
          <MatrixCell state={cell} />
        </td>
      ))}
      {ev.corpOptional.map((cell, i) => (
        <td key={`corp-opt-${i}`} className={dataCellClass('space', i === 0)}>
          <MatrixCell state={cell} />
        </td>
      ))}
    </tr>
  );
}
